import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type NebiTheme = 'perg' | 'noturno';

const STORAGE_KEY = 'nebi-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Pode lançar (Safari privado, cookies bloqueados), então nunca derruba o render. */
const readStored = (): NebiTheme | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'perg' || v === 'noturno' ? v : null;
  } catch {
    return null;
  }
};

const systemTheme = (): NebiTheme =>
  typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches ? 'noturno' : 'perg';

const resolveInitial = (): NebiTheme => readStored() ?? systemTheme();

/** Só troca o atributo: as variáveis já vêm do CSS (`src/styles/nebi/_tokens.scss`). */
const applyTheme = (t: NebiTheme) => {
  const root = document.documentElement;
  root.setAttribute('data-theme', t);
  root.style.colorScheme = t === 'noturno' ? 'dark' : 'light';
};

interface NebiThemeContextValue {
  theme: NebiTheme;
  setTheme: (t: NebiTheme) => void;
  toggleTheme: () => void;
}

const NebiThemeContext = createContext<NebiThemeContextValue | null>(null);

export const NebiThemeProvider = ({ children }: { children: ReactNode }) => {
  // O script inline do `index.html` já aplicou o tema; aqui só alinhamos o estado.
  const [theme, setThemeState] = useState<NebiTheme>(resolveInitial);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Enquanto o usuário não escolher explicitamente, o tema acompanha o sistema.
  useEffect(() => {
    if (readStored()) return;
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (readStored()) return;
      setThemeState(e.matches ? 'noturno' : 'perg');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [theme]);

  const setTheme = useCallback((t: NebiTheme) => {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // sem persistência; o tema ainda vale nesta sessão
    }
    setThemeState(t);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: NebiTheme = prev === 'noturno' ? 'perg' : 'noturno';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
      }
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <NebiThemeContext.Provider value={value}>{children}</NebiThemeContext.Provider>;
};

export const useNebiTheme = (): NebiThemeContextValue => {
  const ctx = useContext(NebiThemeContext);
  if (!ctx) throw new Error('useNebiTheme precisa estar dentro de <NebiThemeProvider>');
  return ctx;
};

export default NebiThemeProvider;
